import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import useApi from 'hooks/useApi';
import { RecordSmsConsentPayload } from 'types/legal';
import { errorMessage, unwrapEnvelope } from './legalUtils';

const NOTE_MIN = 5;
const NOTE_MAX = 500;

interface RecordSmsConsentDialogProps {
  open: boolean;
  onClose: () => void;
  onRecorded: () => void;
  resourceUid: string;
  action: RecordSmsConsentPayload['action'];
  personName?: string;
  phone?: string;
}

const COPY = {
  GRANTED: {
    title: 'Record SMS consent',
    covers: 'Consent will cover',
    noteLabel: 'How was consent obtained?',
    placeholder: 'e.g. Signed the onboarding form on [date]',
    confirm: 'I confirm this person agreed to receive SMS notifications about their shipments/transactions.',
    submit: 'Record consent',
    done: 'SMS consent recorded'
  },
  WITHDRAWN: {
    title: 'Record SMS withdrawal',
    covers: 'Withdrawal applies to',
    noteLabel: 'How was the withdrawal requested?',
    placeholder: 'e.g. Asked the dispatcher by phone on [date]',
    confirm: 'I confirm this person asked to stop receiving SMS notifications about their shipments/transactions.',
    submit: 'Record withdrawal',
    done: 'SMS withdrawal recorded'
  }
};

// For people who never log in: an admin records what the person agreed to, with
// evidence. Records are append-only — a mistake is corrected by recording the opposite.
const RecordSmsConsentDialog: React.FC<RecordSmsConsentDialogProps> = ({
  open,
  onClose,
  onRecorded,
  resourceUid,
  action,
  personName,
  phone
}) => {
  const { recordResourceSmsConsent, showToast } = useApi();
  const [note, setNote] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const copy = COPY[action];

  useEffect(() => {
    if (!open) return;
    setNote('');
    setConfirmed(false);
    setError(null);
  }, [open, action]);

  const noteLength = note.trim().length;
  const noteValid = noteLength >= NOTE_MIN && noteLength <= NOTE_MAX;
  const canSubmit = noteValid && confirmed && !saving;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    setError(null);
    try {
      unwrapEnvelope(await recordResourceSmsConsent(resourceUid, { action, evidenceNote: note.trim() }));
      showToast(copy.done, 'success');
      onRecorded();
      onClose();
    } catch (err: any) {
      setError(errorMessage(err, 'Could not save this record.'));
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (!saving) onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth aria-labelledby="record-sms-title">
      <DialogTitle id="record-sms-title" sx={{ pr: 7 }}>
        {copy.title}
        <Typography variant="body2" color="text.secondary" component="span" sx={{ display: 'block' }} data-no-translate="true">
          {[personName, phone ? `${copy.covers} ${phone}` : ''].filter(Boolean).join(' · ')}
        </Typography>
        <IconButton aria-label="Close" onClick={handleClose} sx={{ position: 'absolute', right: 12, top: 12 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <TextField
            id="record-sms-note"
            label={copy.noteLabel}
            placeholder={copy.placeholder}
            value={note}
            onChange={(event) => setNote(event.target.value)}
            required
            multiline
            minRows={3}
            fullWidth
            autoFocus
            error={note.length > 0 && !noteValid}
            helperText={
              note.length > 0 && noteLength < NOTE_MIN
                ? `At least ${NOTE_MIN} characters`
                : noteLength > NOTE_MAX
                ? `At most ${NOTE_MAX} characters`
                : `${noteLength}/${NOTE_MAX}`
            }
          />
          <FormControlLabel
            sx={{ alignItems: 'flex-start', mx: 0, minHeight: 44 }}
            control={
              <Checkbox
                checked={confirmed}
                onChange={(event) => setConfirmed(event.target.checked)}
                sx={{ p: 1.25, mt: -0.75, ml: -1.25 }}
              />
            }
            label={
              <Typography variant="body2" component="span" sx={{ display: 'block', pt: 0.5 }}>
                {copy.confirm}
              </Typography>
            }
          />
          <Typography variant="body2" color="text.secondary">
            Saved with your name and the time. Records can't be edited or deleted; record a{' '}
            {action === 'GRANTED' ? 'withdrawal' : 'new consent'} instead.
          </Typography>
          {error && (
            <Alert severity="error" role="alert">
              {error}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button variant="outlined" color="secondary" onClick={handleClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color={action === 'WITHDRAWN' ? 'warning' : 'primary'}
          onClick={handleSubmit}
          disabled={!canSubmit}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {copy.submit}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RecordSmsConsentDialog;
